import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

import { isFalseButNotZero } from 'Utilities/helpers';

import type { ListTableValues, MapFieldsToListTableValuesProps, TableDataParams, TableSource } from './ListTable.types';

export const isEmptyAndHidden = (hideIfEmpty: boolean, value: string | string[]): boolean => {
	const hasEmptyValues = isArray(value) ? value.every((v) => isNil(v)) : isNil(value);
	return hideIfEmpty && hasEmptyValues;
};

export const getFieldValue = (value: string | string[], source: TableSource): string | string[] => {
	if (isArray(value)) {
		return value.map((v) => get(source, v));
	}
	return get(source, value);
};

export const mapFieldsToListTableValues = ({
	fields = [],
	source = {},
}: MapFieldsToListTableValuesProps = {}): ListTableValues[] => {
	if (!isArray(fields)) {
		return [];
	}

	if (!isObject(source)) {
		return [];
	}

	return fields.flatMap((field) => {
		const {
			fallback = true,
			fallbackValue = '–',
			format,
			hideCondition,
			hideIfEmpty = false,
			isPII,
			label,
			payload = {},
			render,
			section,
			value,
			valueResolver,
		} = field;

		const fieldValue = getFieldValue(value, source);
		const resolvedValue = isFunction(valueResolver) ? valueResolver(fieldValue) : fieldValue;
		const fieldKey = isArray(value) ? value.join('_') : value;
		const formattedValue = isFunction(format) ? format(resolvedValue) : resolvedValue;
		const isHiddenByCondition = isFunction(hideCondition) ? hideCondition(fieldValue) : false;
		const isHiddenByBeingEmpty = isEmptyAndHidden(hideIfEmpty, fieldValue);

		const renderValue = isFunction(render)
			? render({
					label,
					payload,
					section,
					value: resolvedValue,
			  })
			: formattedValue;

		const fallbackResolvedValue = fallback ? fallbackValue : renderValue;
		const finalValue = isFalseButNotZero(renderValue) ? fallbackResolvedValue : renderValue;

		return !isHiddenByBeingEmpty && !isHiddenByCondition
			? [
					{
						...(isPII ? { isPII } : {}),
						key: fieldKey,
						label,
						value: finalValue,
					},
			  ]
			: [];
	});
};

export const getTableFields = <T>({ data, key, value }: TableDataParams<T>): ListTableValues[] =>
	data.map((entry) => {
		const fieldLabel = String(entry[key]);
		const fieldValue = String(entry[value]);

		return {
			key: fieldLabel,
			label: fieldLabel,
			value: fieldValue,
		};
	});

export const getTableSource = <T>({ data, key, value }: TableDataParams<T>): TableSource =>
	data.reduce((acc: TableSource, entry) => {
		const sourceKey = String(entry[key]);
		const sourceValue = String(entry[value]);
		if (sourceKey && sourceValue) {
			acc[sourceKey] = sourceValue;
		}
		return acc;
	}, {});
